import React, { useCallback } from 'react';
import { useRequireRole, MemberRole } from 'modules/community';
import { View, ActionSheetIOS, TouchableOpacity, Platform } from 'react-native';
import RNBottomSheet from 'react-native-bottomsheet';
import Icon from 'react-native-vector-icons/Ionicons';
import { grey } from 'app/styles/colors';
import { deletePost, flag } from 'modules/post/post.actions';
import { useDispatch } from 'react-redux';
import { push } from 'modules/navigation/navigation.actions';
import { setCreatePostState } from 'modules/createPost/createPost.actions';

type ExtendedSheet = RNBottomSheet & {
  showActionSheetWithOptions?: typeof RNBottomSheet.showBottomSheetWithOptions;
};

let ActionSheet: ExtendedSheet | ActionSheetIOS; //: Platform.OS === 'android' ? ActionSheetIOS || RNBottomSheet;
if (Platform.OS === 'android') {
  ActionSheet = RNBottomSheet as ExtendedSheet;
  ActionSheet.showActionSheetWithOptions = RNBottomSheet.showBottomSheetWithOptions;
} else {
  ActionSheet = ActionSheetIOS;
}

const ownerMenu = {
  buttons: ['Edit', 'Delete', 'Cancel'],
  destructiveIndex: 1,
  cancelIndex: 2,
};

interface PopupProps {
  isOwner: boolean;
  singlePost: boolean;
  post: any;
  userId: string;
  edit?: () => void;
  deleteFn?: () => void;
}

export const Popup = ({
  isOwner,
  post,
  edit,
  userId,
  deleteFn,
  singlePost,
}: PopupProps): JSX.Element => {
  const dispatch = useDispatch();
  const isAdmin = useRequireRole(MemberRole.ADMIN);

  const deletePostDefault = useCallback(() => {
    let redirect = false;
    if (singlePost) redirect = true;
    dispatch(deletePost(post, redirect));
  }, [post, singlePost, dispatch]);

  const toggleEditing = useCallback(() => {
    dispatch(
      setCreatePostState({
        postBody: post.body,
        nativeImage: true,
        postUrl: post.url,
        postImage: post.image,
        allTags: post.tags,
        urlPreview: {
          image: post.image,
          title: post.title ? post.title : 'Untitled',
          description: post.description,
        },
        edit: true,
        editPost: post,
      })
    );
    dispatch(
      push({
        key: 'createPost',
        back: true,
        title: 'Edit Post',
        next: 'Update',
        left: 'Cancel',
      })
    );
  }, [post, dispatch]);

  const showActionSheet = useCallback(() => {
    if (!ActionSheet?.showActionSheetWithOptions) return;
    if (isOwner) {
      return ActionSheet.showActionSheetWithOptions(
        {
          options: ownerMenu.buttons,
          cancelButtonIndex: ownerMenu.cancelIndex,
          destructiveButtonIndex: ownerMenu.destructiveIndex,
        },
        (buttonIndex: number) => {
          switch (buttonIndex) {
            case 0:
              if (edit != null) edit();
              else toggleEditing();
              break;
            case 1:
              if (deleteFn != null) deleteFn();
              else deletePostDefault();
              break;
            default:
          }
        }
      );
    }

    return ActionSheet.showActionSheetWithOptions(
      {
        options: isAdmin
          ? ['Flag Inappropriate Content', 'Audit User', 'Cancel']
          : ['Flag Inappropriate Content', 'Cancel'],
        cancelButtonIndex: isAdmin ? 2 : 1,
      },
      (buttonIndex: number) => {
        switch (buttonIndex) {
          case 0:
            dispatch(flag(post));
            break;
          case 1:
            {
              if (!isAdmin) break;
              dispatch(
                push({
                  key: 'relatedUsers',
                  back: true,
                  title: 'Related Users',
                  id: userId,
                })
              );
            }
            break;
          default:
        }
      }
    );
  }, [
    post,
    deletePostDefault,
    deleteFn,
    toggleEditing,
    edit,
    dispatch,
    isOwner,
    isAdmin,
    userId,
  ]);

  return (
    <View
      style={{
        justifyContent: 'flex-end',
        overflow: 'visible',
        alignSelf: 'center',
      }}
    >
      <TouchableOpacity style={{ paddingRight: 10 }} onPress={showActionSheet}>
        <Icon name="ellipsis-horizontal" size={24} color={grey} />
      </TouchableOpacity>
    </View>
  );
};
